import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Rocket, FileSpreadsheet, Users } from "lucide-react";

const audiences = [
  {
    icon: Rocket,
    title: "Startups y emprendedores",
    description: "Que necesitan su primera plataforma funcional para clientes.",
    tag: "Startups",
  },
  {
    icon: FileSpreadsheet,
    title: "PYMEs",
    description: "Que quieren dejar atrás procesos manuales y hojas de cálculo.",
    tag: "PYMEs",
  },
  {
    icon: Users,
    title: "Equipos en crecimiento",
    description: "Que buscan centralizar información y coordinar mejor su operación.",
    tag: "Equipos",
  },
];

const AudienceSection = ({ onApplyClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="audiencia"
      data-testid="audience-section"
      ref={ref}
      className="py-24 sm:py-32"
      style={{ backgroundColor: "#0A0A0A" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono tracking-[0.25em] uppercase text-neutral-500 block mb-4">
            Para quién
          </span>
          <h2
            data-testid="audience-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            ¿Para qué tipo de empresas es esta oportunidad?
          </h2>
          <p className="text-base sm:text-lg text-neutral-400 leading-relaxed mt-6 max-w-2xl">
            Esta campaña está pensada para empresas que tienen claro un reto de negocio,
            están listas para usar una solución de software en su operación diaria, y
            valoran trabajar con un aliado tecnológico que piensa en el crecimiento del negocio.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {audiences.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 * (i + 1) }}
              className="group relative bg-[#050505] border border-white/[0.06] hover:border-white/20 p-8 sm:p-10 transition-all duration-300"
            >
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-neutral-600 mb-6 block">
                {item.tag}
              </span>
              <item.icon className="w-8 h-8 text-white mb-6 opacity-50 group-hover:opacity-100 transition-opacity" />
              <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
                {item.title}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 flex flex-col items-center text-center"
        >
          <button
            data-testid="audience-cta-btn"
            onClick={onApplyClick}
            className="bg-white text-black font-bold text-sm tracking-[0.15em] uppercase px-10 py-5 hover:bg-neutral-200 transition-all active:scale-95"
          >
            Aplicar ahora al desarrollo gratis
          </button>
          <p className="text-xs text-neutral-500 mt-4">
            Cuéntanos de tu proyecto y agenda una sesión de diagnóstico.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AudienceSection;
